import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { uploadPublicAsset } from "@/lib/upload";
import { StepTitle, NavButtons } from "@/components/onboard/ui";
import { Camera, ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function StepSelfie({ token, state, onSaved, onBack }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [camError, setCamError] = useState("");
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(false);
  const already = state.wizard.selfie?.url;

  const start = async () => {
    setCamError(""); setShot(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 720, height: 720 }, audio: false });
      streamRef.current = s; setLive(true);
      if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); }
    } catch (e) { setCamError("We couldn't access your camera. Please allow camera access in your browser and try again."); }
  };
  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setLive(false); };
  useEffect(() => { if (!already) start(); return stop; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    ctx.translate(c.width, 0); ctx.scale(-1, 1); ctx.drawImage(v, 0, 0);
    c.toBlob((b) => setShot({ blob: b, url: URL.createObjectURL(b) }), "image/jpeg", 0.9);
    stop();
  };

  const save = async () => {
    if (!shot && already) { onSaved(state, true); return; }
    if (!shot) return;
    setLoading(true);
    try {
      const asset = await uploadPublicAsset(new File([shot.blob], "live-selfie.jpg", { type: "image/jpeg" }), token, "carrier_onboarding");
      const { data } = await api.post(`/onboard/${token}/save`, { step: "selfie", data: { url: asset.url, taken_at: new Date().toISOString() } });
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <StepTitle kicker="Step 5 · Identity Check" title="Take a Live Selfie" sub="This confirms the real owner is completing onboarding and protects your MC from identity fraud. Face the camera in good light." />
      <div className="relative mx-auto w-full max-w-sm aspect-square rounded-[2rem] overflow-hidden border border-white/10 bg-black" data-testid="ob-selfie-frame">
        {shot ? <img src={shot.url} alt="Selfie" className="w-full h-full object-cover" /> : already && !live ? (
          <div className="w-full h-full grid place-items-center text-center p-6"><div><CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto" /><p className="text-white mt-3 font-semibold">Selfie Captured</p></div></div>
        ) : <video ref={videoRef} playsInline muted className="w-full h-full object-cover -scale-x-100" />}
        {!shot && !camError && <div className="pointer-events-none absolute inset-8 rounded-full border-2 border-dashed border-sky-300/60" />}
      </div>
      {camError && <p className="mt-4 text-sm text-amber-300 text-center" data-testid="ob-cam-error">{camError}</p>}
      <div className="mt-6 flex justify-center gap-3">
        {shot || (already && !live) ? (
          <button onClick={start} className="h-12 px-6 rounded-full border border-white/15 text-white/80 inline-flex items-center gap-2 hover:border-white/40" data-testid="ob-selfie-retake"><ArrowCounterClockwise size={16} />Retake</button>
        ) : (
          <button onClick={capture} className="h-14 w-14 rounded-full bg-white grid place-items-center shadow-[0_0_0_6px_rgba(255,255,255,.15)] hover:scale-105 transition-transform" data-testid="ob-selfie-capture"><Camera size={24} weight="fill" className="text-slate-900" /></button>
        )}
      </div>
      <NavButtons onBack={onBack} onNext={save} loading={loading} disabled={!shot && !already} nextLabel="Use This Selfie" />
    </div>
  );
}
